import { api } from '../../core/network/api';

/** A seeded era tier (gangster ladder), highest standing = highest minPoints. */
export interface EraTier {
  tierName: string;
  minPoints: number;
  rewardSoft: number; // cash
  rewardPremium: number; // runes
  rewardXp: number;
  /** Paid the moment the rank is reached (once per saga) — "every promotion pays out". */
  promotionSoft: number;
  promotionPremium: number;
}

/** GET /era — the current era + the caller's standing within it. */
export interface EraView {
  era: {
    id: string;
    name: string;
    number: number;
    endsAt: string;
    status: string;
  };
  player: {
    rankPoints: number;
    tier: string;
    nextTier: string | null;
    nextThreshold: number | null;
    pointsToNext: number;
    progressPct: number;
  };
  tiers: EraTier[];
  /** Promotions PAID by this very call — celebrate them, they're already credited. */
  newlyPromoted: { tier: string; soft: number; premium: number }[];
}

/** One row in a leaderboard. */
export interface LeaderboardRow {
  rank: number;
  userId: string;
  username: string | null;
  /** Clan tag, when the player farms under one (QA item 8). */
  tag: string | null;
  value: number;
  isMe: boolean;
}

/** A selectable leaderboard category. */
export interface LeaderboardCategoryOption {
  key: string;
  label: string;
}

/** GET /leaderboard?category=… — top N + the caller's personal rank. */
export interface LeaderboardView {
  category: string;
  label: string;
  categories: LeaderboardCategoryOption[];
  top: LeaderboardRow[];
  /**
   * Me with one neighbour either side (…, 635, 636(me), 637), sent ONLY when the
   * caller ranks past `top` and actually appears on the board. Null when already
   * visible in `top`, or when the caller has no standing at all.
   */
  around: LeaderboardRow[] | null;
  /** My own standing. `username` is sent so the "me" row carries my name
   *  instead of a generic label; rank 0 means "no standing on this board". */
  me: { rank: number; value: number; total: number; username: string | null };
}

/** GET /api/era — current era + the player's points / tier / progress. */
export const fetchEra = () => api.get<EraView>('/era');

/** GET /api/leaderboard?category=… — top players + the caller's rank. */
export const fetchLeaderboard = (category: string) =>
  api.get<LeaderboardView>(`/leaderboard?category=${encodeURIComponent(category)}`);
